/**
 * Countries a studio or creator can register from.
 *
 * **Curated, and curated on a real constraint.** GOODBUILD pays studios and
 * creators through Stripe Connect, so the list is the countries Stripe can
 * create a connected account in. Offering the full ISO 3166 list would let
 * somebody complete an onboarding we then cannot pay out from, and they would
 * find out at the point they expected money — which is the worst possible
 * moment and entirely avoidable by not asking the question that way.
 *
 * This is a snapshot of Stripe's availability list rather than a live query;
 * Stripe has no API for it. Re-check it against
 * https://stripe.com/global when a country is added, and note that a country
 * being here means Stripe supports a connected account there, not that
 * GOODBUILD has confirmed it can trade there.
 *
 * `EU_MEMBERS` is separate because the legal and tax consequences differ — VAT
 * treatment, consumer rights, and which document set an onboarding has to show.
 *
 * **The constraint above is now only half true, and the list is kept anyway**
 * (#122). Studios are no longer paid through Stripe Connect — royalties go by
 * wire, sent by hand out of Revolut — so for a studio the binding question is
 * where Revolut can send a transfer rather than where Stripe can open an
 * account, and those are not the same list. Creators still go through a
 * provider, and the registration forms, the bank-country picker on
 * `/portal/payouts` and `isSupportedCountry` on three endpoints all read this
 * one list.
 *
 * Widening it is therefore a real decision rather than an oversight: it would
 * let somebody register from a country the creator rail cannot reach, and it
 * wants checking against what we can actually send to before anybody adds a
 * row. Until then this stays the honest floor — every country here is one both
 * rails can pay — and `bankSchemeFor` in `lib/payouts/developer-methods.ts`
 * decides which transfer form each of them needs.
 */

export interface Country {
  /** ISO 3166-1 alpha-2, which is what the database column stores. */
  readonly code: string;
  readonly name: string;
}

/** Sorted by name, which is the order the picker shows them in. */
export const COUNTRIES: readonly Country[] = [
  { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'CA', name: 'Canada' },
  { code: 'HR', name: 'Croatia' },
  { code: 'CY', name: 'Cyprus' },
  { code: 'CZ', name: 'Czechia' },
  { code: 'DK', name: 'Denmark' },
  { code: 'EE', name: 'Estonia' },
  { code: 'FI', name: 'Finland' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'GI', name: 'Gibraltar' },
  { code: 'GR', name: 'Greece' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'HU', name: 'Hungary' },
  { code: 'IE', name: 'Ireland' },
  { code: 'IT', name: 'Italy' },
  { code: 'JP', name: 'Japan' },
  { code: 'LV', name: 'Latvia' },
  { code: 'LI', name: 'Liechtenstein' },
  { code: 'LT', name: 'Lithuania' },
  { code: 'LU', name: 'Luxembourg' },
  { code: 'MT', name: 'Malta' },
  { code: 'MX', name: 'Mexico' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'NO', name: 'Norway' },
  { code: 'PL', name: 'Poland' },
  { code: 'PT', name: 'Portugal' },
  { code: 'RO', name: 'Romania' },
  { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'ES', name: 'Spain' },
  { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'US', name: 'United States' },
];

const BY_CODE = new Map(COUNTRIES.map((country) => [country.code, country]));

export function countryByCode(code: string | null | undefined): Country | null {
  if (!code) return null;
  return BY_CODE.get(code.toUpperCase()) ?? null;
}

export function isSupportedCountry(code: string | null | undefined): boolean {
  return countryByCode(code) !== null;
}

export function countryName(code: string | null | undefined): string | null {
  return countryByCode(code)?.name ?? null;
}

/**
 * EU member states, for the rules that follow from membership rather than from
 * geography — VAT treatment and which consumer-rights wording applies.
 *
 * Norway, Liechtenstein and Switzerland are deliberately absent: the first two
 * are EEA but not EU, and Switzerland is neither.
 */
export const EU_MEMBERS: ReadonlySet<string> = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
]);

export function isEuMember(code: string | null | undefined): boolean {
  return code ? EU_MEMBERS.has(code.toUpperCase()) : false;
}

/**
 * Every country there is, for the question every player is asked.
 *
 * **The opposite policy to `COUNTRIES` above, deliberately.** That list is
 * curated on a real constraint — where a payout rail can send money — and is
 * the right answer to *where can we onboard a creator from*. It is the wrong
 * answer to *where are you from*, which is what sign-up asks: a Brazilian or
 * an Indonesian player picking their country off a list of eighty would be
 * told, by a shop wanting their money, that their country is not one of the
 * options. So this is ISO 3166-1 alpha-2's officially assigned set, entire.
 *
 * **Codes only, no names.** The picker renders them through
 * `Intl.DisplayNames`, which every browser and both webviews have had for
 * years and which names them in the reader's own language rather than in
 * English — so a list nobody has to translate is also a list nobody has to
 * maintain. `COUNTRIES` keeps its names because they are part of a form whose
 * wording is contractual; these are a menu.
 *
 * **This is not a trading-permission list and must never be read as one.** It
 * says a country exists, not that we may sell there, and nothing about
 * sanctions, tax registration or age of majority follows from an entry here.
 * What a player declares is a self-reported fact about themselves; what may
 * actually be sold to them is decided at checkout by the payment provider,
 * which is the party that holds that obligation.
 *
 * The uninhabited and near-uninhabited entries — Antarctica, Bouvet Island,
 * the French Southern Territories — are kept rather than pruned. Pruning is
 * curation, curation invites the argument above, and the cost of leaving them
 * is that somebody may pick one as a joke. Nothing depends on the answer today
 * that a joke would break.
 */
export const ISO_COUNTRY_CODES: readonly string[] = [
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AT',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BE',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CH',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'CZ',
  'DE',
  'DJ',
  'DK',
  'DM',
  'DO',
  'DZ',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'FR',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LU',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NL',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PL',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'RE',
  'RO',
  'RS',
  'RU',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
];

const ISO_CODES = new Set(ISO_COUNTRY_CODES);

/**
 * Whether this is a country at all.
 *
 * The check the sign-up endpoint runs, and the whole of what the server can
 * say about a declared country: the list is closed, so a typo and an invention
 * are both refused, and nothing beyond existence is asserted. Compare
 * `isSupportedCountry`, which answers a different question — whether we can pay
 * somebody there — and must not be reached for by mistake in this one's place.
 */
export function isIsoCountry(code: string | null | undefined): boolean {
  return code ? ISO_CODES.has(code.toUpperCase()) : false;
}
